/**
 * @fileoverview Syndicator Showdown — Share Result Modal
 * @module components/share/ShareResultModal
 *
 * Thin wrapper around the unified ShareQrModal for sharing a single race
 * result (winner / top-5 placing) or an overall leaderboard rank. Reuses —
 * rather than duplicates — the existing public stable share page as the
 * link destination (getOrCreateStableShareForEntry), since that page
 * already shows live progress/winnings/rank once entries close. No
 * separate /share/result/:shareId backend needed.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getOrCreateStableShareForEntry } from '../../services/stableShareService';
import { buildStableShareUrl } from '../../utils/shareLinks';
import { getPublicName, getProfileImageUrl } from '../../utils/profileImage';
import { getResultArtworkType } from '../../utils/shareArtwork';
import { buildResultShareText, buildLeaderboardShareText, fmtMoney, ordinal } from '../../utils/shareCopy';
import ShareQrModal from './ShareQrModal';
import ShareResultCard from './ShareResultCard';

function buildCardCopy({ resultType, stableName, runnerName, placingText, finishingPosition, raceNumber, showdownName, virtualPayout, rank, winnings }) {
  if (resultType === 'leaderboard') {
    return {
      artworkType: 'leaderboard',
      eyebrow: 'LEADERBOARD RANK',
      title: `${stableName} is ranked ${ordinal(rank) || '—'}`,
      body: showdownName || null,
      statValue: fmtMoney(winnings) || '$0',
      statLabel: 'Virtual Winnings',
      shareText: buildLeaderboardShareText({ stableName, rank, showdownName, winnings }),
    };
  }

  const won = finishingPosition === 1;
  const raceLabel = raceNumber ? `Race ${raceNumber}` : 'the race';
  return {
    artworkType: getResultArtworkType(finishingPosition),
    eyebrow: 'SHOWDOWN RESULT',
    title: won ? `Winner for ${stableName}!` : `${stableName}'s stable is on the board!`,
    body: won
      ? `${runnerName} won ${raceLabel}${showdownName ? ` in ${showdownName}` : ''}.`
      : `${runnerName} finished ${placingText || ordinal(finishingPosition) || 'in the money'} in ${raceLabel}${showdownName ? ` in ${showdownName}` : ''}.`,
    statValue: fmtMoney(virtualPayout) || '$0',
    statLabel: 'Virtual Winnings',
    shareText: buildResultShareText({
      stableName, runnerName, placingText, finishingPosition, raceNumber, showdownName, virtualPayout,
    }),
  };
}

export default function ShareResultModal({
  isOpen,
  onClose,
  user,
  userDoc,
  entry,
  resultType = 'winner', // 'winner' | 'top5' | 'leaderboard'
  runnerName,
  placingText,
  finishingPosition,
  raceNumber,
  showdownName,
  virtualPayout,
  rank,
  winnings,
}) {
  const [shareId, setShareId] = useState(null);
  const [error, setError]     = useState(false);

  useEffect(() => {
    if (!isOpen || shareId || !user || !entry) return;
    let cancelled = false;
    setError(false);
    getOrCreateStableShareForEntry({ user, userDoc, entry })
      .then((id) => { if (!cancelled) setShareId(id); })
      .catch((err) => {
        console.error('[ShareResultModal] getOrCreateStableShareForEntry failed:', err?.code, err?.message);
        if (!cancelled) setError(true);
      });
    return () => { cancelled = true; };
  }, [isOpen, shareId, user, userDoc, entry]);

  const stableName = getPublicName(userDoc, user);
  const copy = buildCardCopy({
    resultType, stableName, runnerName, placingText, finishingPosition, raceNumber, showdownName, virtualPayout, rank, winnings,
  });

  const shareUrl = shareId ? buildStableShareUrl(shareId) : null;

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <ShareQrModal
      isOpen={isOpen}
      onClose={handleClose}
      url={shareUrl || ''}
      shareId={shareId}
      title="Share Result"
      description={error
        ? 'Couldn\u2019t prepare a share link right now — please try again.'
        : 'Anyone with this link can see this result — no login required.'}
      shareTitle={copy.title}
      shareText={copy.shareText}
      previewComponent={<ShareResultCard {...copy} compact />}
    />
  );
}
